
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';
import { 
  collection, query, where, onSnapshot, 
  addDoc, serverTimestamp, doc, setDoc, updateDoc, 
  limit, getDocs, deleteDoc, orderBy,
  writeBatch, QueryDocumentSnapshot
} from 'firebase/firestore';
import { Card, Button, Badge } from '../components/ui';

interface BroadcastSession {
  id: string;
  hostId: string;
  hostName: string;
  status: 'live' | 'ended';
  startTime: any;
  endTime?: any;
  title: string;
}

interface Question {
  id: string;
  uid: string;
  userName: string;
  text: string;
  createdAt: any;
  isAnswered: boolean;
  isHighlighted: boolean;
  broadcastId: string;
}

const rtcConfig = {
  iceServers: [
    { urls: ['stun:stun1.l.google.com:19302', 'stun:stun2.l.google.com:19302'] }
  ],
  iceCandidatePoolSize: 0,
};

const Broadcast = () => {
  const { userProfile, currentUser } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [activeSession, setActiveSession] = useState<BroadcastSession | null>(null);
  const [lastEndedSession, setLastEndedSession] = useState<BroadcastSession | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [viewerCount, setViewerCount] = useState(0);
  const [newQuestion, setNewQuestion] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [streamError, setStreamError] = useState('');
  const [scheduledInfo, setScheduledInfo] = useState<{name: string, time: string, description: string} | null>(null);
  
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const [isLocalBroadcaster, setIsLocalBroadcaster] = useState(false);
  const [isMediaReady, setIsMediaReady] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'idle' | 'connecting' | 'connected' | 'failed'>('idle');
  const [playBlocked, setPlayBlocked] = useState(false);

  const videoElementRef = useRef<HTMLVideoElement | null>(null);
  const peerConnections = useRef<Record<string, RTCPeerConnection>>({});
  const unsubscribers = useRef<Record<string, Array<() => void>>>({});
  
  const [guestInvite, setGuestInvite] = useState<any>(null);
  const [hasEnteredBackstage, setHasEnteredBackstage] = useState(false);
  const [isVerifyingInvite, setIsVerifyingInvite] = useState(false);

  // 1. Sync Broadcast Schedule
  useEffect(() => {
    const unsubSched = onSnapshot(doc(db, 'config', 'broadcast_schedule'), (snap) => {
        if (snap.exists()) {
            const data = snap.data();
            if (data.nextSpeaker) {
                setScheduledInfo({
                    name: data.nextSpeaker,
                    time: data.scheduledAt || 'TBA',
                    description: data.description || ''
                });
            } else {
                setScheduledInfo(null);
            }
        } else {
            setScheduledInfo(null);
        }
    });
    return () => unsubSched();
  }, []);

  // 2. FORCED VIDEO ATTACHMENT
  const syncVideoStream = useCallback(() => {
    const el = videoElementRef.current;
    if (!el) return;

    const streamToAttach = isLocalBroadcaster ? localStream : remoteStream;
    if (streamToAttach) {
      if (el.srcObject !== streamToAttach) {
        el.srcObject = streamToAttach;
        el.onloadedmetadata = () => {
          el.play().catch(e => {
            console.warn("Autoplay block:", e);
            if (!isLocalBroadcaster) setPlayBlocked(true);
          });
        };
      } else if (el.paused && !el.ended) {
          el.play().catch(e => {
            if (!isLocalBroadcaster) setPlayBlocked(true);
          });
      }
    } else {
      el.srcObject = null;
    }
  }, [localStream, remoteStream, isLocalBroadcaster]);

  useEffect(() => {
    syncVideoStream();
  }, [syncVideoStream]);

  // Health check to ensure video keeps playing
  useEffect(() => {
    const timer = setInterval(() => {
      if (videoElementRef.current && (remoteStream || localStream)) {
        if (videoElementRef.current.paused && !videoElementRef.current.ended) {
            syncVideoStream();
        }
      }
    }, 3000);
    return () => clearInterval(timer);
  }, [remoteStream, localStream, syncVideoStream]);

  // 3. Invite Verification
  useEffect(() => {
    const inviteToken = searchParams.get('invite');
    if (inviteToken) {
      setIsVerifyingInvite(true);
      getDocs(query(collection(db, 'speaker_invites'), where('token', '==', inviteToken)))
        .then(snap => {
          if (!snap.empty) {
            const data = snap.docs[0].data();
            if (data.status === 'used') {
               setStreamError("This invite link has already been used and the broadcast has ended.");
            } else {
               setGuestInvite({ id: snap.docs[0].id, ...data });
            }
          } else {
            setStreamError("Access link expired.");
          }
        })
        .finally(() => setIsVerifyingInvite(false));
    }
  }, [searchParams]);

  // 4. Broadcast Session Sync
  useEffect(() => {
    const q = query(collection(db, 'broadcast_sessions'), where('status', '==', 'live'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
        if (!snapshot.empty) {
            const docs = snapshot.docs.map((d: QueryDocumentSnapshot) => ({ id: d.id, ...d.data() } as BroadcastSession));
            docs.sort((a: BroadcastSession, b: BroadcastSession) => (b.startTime?.toMillis?.() || 0) - (a.startTime?.toMillis?.() || 0));
            const best = docs[0];
            setActiveSession(best);
            setLastEndedSession(null);
            const myId = guestInvite?.token || userProfile?.uid || 'anonymous';
            setIsLocalBroadcaster(best.hostId === myId);
        } else {
            setActiveSession(null);
            setIsLocalBroadcaster(false);
            setRemoteStream(null);
            setConnectionStatus('idle');
            setViewerCount(0);
        }
    });
    return () => unsubscribe();
  }, [guestInvite, userProfile]);

  // 5. Presence & Questions Logic (Real-time sync)
  useEffect(() => {
    if (!activeSession?.id) {
        setQuestions([]);
        return;
    }

    const q = query(collection(db, 'broadcast_questions'), where('broadcastId', '==', activeSession.id));
    const unsubQs = onSnapshot(q, (snap) => {
      const qs = snap.docs.map(d => ({ id: d.id, ...d.data() } as Question));
      qs.sort((a: Question, b: Question) => {
          const timeA = a.createdAt?.toMillis?.() || Date.now();
          const timeB = b.createdAt?.toMillis?.() || Date.now();
          return timeB - timeA;
      });
      setQuestions(qs);
    });

    const unsubParticipants = onSnapshot(collection(db, `broadcast_sessions/${activeSession.id}/participants`), (snap) => {
        setViewerCount(snap.size);
    });

    return () => {
        unsubQs();
        unsubParticipants();
    };
  }, [activeSession?.id]);

  // 6. HOST SIGNALING
  useEffect(() => {
    if (!activeSession?.id || !isLocalBroadcaster || !localStream) return;

    const sessionRefId = activeSession.id;
    const unsubscribe = onSnapshot(collection(db, `broadcast_sessions/${sessionRefId}/participants`), (snapshot) => {
      snapshot.docChanges().forEach(async (change) => {
        const participantId = change.doc.id;
        
        if (change.type === 'added') {
          if (peerConnections.current[participantId]) return;

          const pc = new RTCPeerConnection(rtcConfig);
          peerConnections.current[participantId] = pc;
          localStream.getTracks().forEach(track => pc.addTrack(track, localStream));

          pc.onicecandidate = (event) => {
            if (event.candidate && pc.connectionState !== 'connected') {
              addDoc(collection(db, `broadcast_sessions/${sessionRefId}/participants/${participantId}/hostCandidates`), event.candidate.toJSON())
                .catch(() => {});
            }
          };

          const offer = await pc.createOffer();
          await pc.setLocalDescription(offer);
          await updateDoc(change.doc.ref, { offer: { type: offer.type, sdp: offer.sdp } });

          const unsubAnswer = onSnapshot(change.doc.ref, (docSnap) => {
            const data = docSnap.data();
            if (data?.answer && pc.signalingState !== 'stable') {
              pc.setRemoteDescription(new RTCSessionDescription(data.answer)).catch(() => {});
            }
          });

          const unsubViewerCands = onSnapshot(collection(db, `broadcast_sessions/${sessionRefId}/participants/${participantId}/viewerCandidates`), (cSnap) => {
            cSnap.docChanges().forEach(cChange => {
              if (cChange.type === 'added') {
                pc.addIceCandidate(new RTCIceCandidate(cChange.doc.data())).catch(() => {});
              }
            });
          });

          unsubscribers.current[participantId] = [unsubAnswer, unsubViewerCands];

          pc.onconnectionstatechange = () => {
            if (['failed', 'closed', 'disconnected'].includes(pc.connectionState)) {
              unsubscribers.current[participantId]?.forEach(unsub => unsub());
              delete peerConnections.current[participantId];
            }
          };
        } else if (change.type === 'removed') {
            if (peerConnections.current[participantId]) {
                peerConnections.current[participantId].close();
                delete peerConnections.current[participantId];
            }
            unsubscribers.current[participantId]?.forEach(unsub => unsub());
        }
      });
    });

    return () => {
      unsubscribe();
      (Object.values(peerConnections.current) as RTCPeerConnection[]).forEach(pc => pc.close());
      peerConnections.current = {};
      (Object.values(unsubscribers.current) as Array<Array<() => void>>).forEach(subs => subs.forEach(s => s()));
    };
  }, [activeSession?.id, isLocalBroadcaster, localStream]);

  // 7. VIEWER SIGNALING
  useEffect(() => {
    if (!activeSession?.id || isLocalBroadcaster) return;

    let pc: RTCPeerConnection | null = null;
    let unsubOffer: (() => void) | null = null;
    let unsubHostCands: (() => void) | null = null;
    const sessionRefId = activeSession.id;
    const participantId = currentUser?.uid || `guest_${Math.random().toString(36).substring(7)}`;
    let hasSentAnswer = false;

    const startHandshake = async () => {
      setConnectionStatus('connecting');
      pc = new RTCPeerConnection(rtcConfig);
      
      pc.ontrack = (event) => {
        if (event.streams && event.streams[0]) {
          setRemoteStream(event.streams[0]);
          setConnectionStatus('connected');
        }
      };

      const participantRef = doc(db, `broadcast_sessions/${sessionRefId}/participants`, participantId);
      await setDoc(participantRef, {
        uid: currentUser?.uid || participantId,
        name: userProfile?.nickname || userProfile?.displayName || 'Viewer',
        joinedAt: serverTimestamp()
      });

      pc.onicecandidate = (event) => {
        if (event.candidate && pc?.connectionState !== 'connected') {
          addDoc(collection(db, `broadcast_sessions/${sessionRefId}/participants/${participantId}/viewerCandidates`), event.candidate.toJSON())
            .catch(() => {});
        }
      };

      unsubOffer = onSnapshot(participantRef, async (snap) => {
        const data = snap.data();
        if (data?.offer && pc && pc.signalingState === 'stable' && !hasSentAnswer) {
          hasSentAnswer = true; 
          await pc.setRemoteDescription(new RTCSessionDescription(data.offer));
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          await updateDoc(participantRef, { answer: { type: answer.type, sdp: answer.sdp } });
        }
      });

      unsubHostCands = onSnapshot(collection(db, `broadcast_sessions/${sessionRefId}/participants/${participantId}/hostCandidates`), (cSnap) => {
        cSnap.docChanges().forEach(cChange => {
          if (cChange.type === 'added' && pc) {
            pc.addIceCandidate(new RTCIceCandidate(cChange.doc.data())).catch(() => {});
          }
        });
      });
    };

    startHandshake();

    return () => {
      if (pc) pc.close();
      if (unsubOffer) unsubOffer();
      if (unsubHostCands) unsubHostCands();
      deleteDoc(doc(db, `broadcast_sessions/${sessionRefId}/participants`, participantId)).catch(() => {});
    };
  }, [activeSession?.id, isLocalBroadcaster, currentUser?.uid]);

  const handleEnterBackstage = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      setLocalStream(stream);
      setIsMediaReady(true);
      setHasEnteredBackstage(true);
    } catch (err) {
      setStreamError("Media blocked. Check browser permissions.");
    }
  };

  const handleGoLive = async () => {
    if (!isMediaReady || activeSession) return;
    try {
      const liveSnap = await getDocs(query(collection(db, 'broadcast_sessions'), where('status', '==', 'live')));
      const batch = writeBatch(db);
      liveSnap.forEach(d => batch.update(d.ref, { status: 'ended', endTime: serverTimestamp() }));
      await batch.commit();

      const name = guestInvite ? guestInvite.name : (userProfile?.displayName || "Speaker");
      const id = guestInvite ? guestInvite.token : (userProfile?.uid || "admin");
      
      const newRef = doc(collection(db, 'broadcast_sessions'));
      await setDoc(newRef, {
        hostId: id, hostName: name, status: 'live',
        startTime: serverTimestamp(),
        title: guestInvite ? guestInvite.title : "Live Support Q&A"
      });
      
      if (guestInvite) await updateDoc(doc(db, 'speaker_invites', guestInvite.id), { status: 'active' });
    } catch (err) {
      setStreamError("Stream failed to start.");
    }
  };

  const handleEndBroadcast = async () => {
    if (!activeSession || !isLocalBroadcaster) return;
    try {
      await updateDoc(doc(db, 'broadcast_sessions', activeSession.id), { status: 'ended', endTime: serverTimestamp() });
      if (guestInvite) {
         await updateDoc(doc(db, 'speaker_invites', guestInvite.id), { status: 'used' });
      }
      if (localStream) localStream.getTracks().forEach(t => t.stop());
      setLocalStream(null);
      setIsMediaReady(false);
      setHasEnteredBackstage(false);
    } catch (err) {}
  };

  const handleSubmitQuestion = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newQuestion.trim() || !activeSession || isSubmitting) return;
    
    setIsSubmitting(true);
    const currentBroadcastId = activeSession.id;

    try {
      await addDoc(collection(db, 'broadcast_questions'), {
        broadcastId: currentBroadcastId,
        uid: userProfile?.uid || 'guest',
        userName: userProfile?.nickname || userProfile?.displayName || 'Anonymous',
        text: newQuestion.trim(),
        createdAt: serverTimestamp(),
        isAnswered: false,
        isHighlighted: false
      });
      setNewQuestion('');
    } catch (err: any) {
      console.error("Failed to post question:", err);
      if (err.code === 'resource-exhausted') {
          alert("The server is busy. Please wait a moment and try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleHighlight = async (qId: string, current: boolean) => {
    if (!isLocalBroadcaster) return;
    const highlighted = questions.find(q => q.isHighlighted);
    if (highlighted && highlighted.id !== qId) {
        await updateDoc(doc(db, 'broadcast_questions', highlighted.id), { isHighlighted: false });
    }
    await updateDoc(doc(db, 'broadcast_questions', qId), { isHighlighted: !current });
  };

  const markQuestionDone = async (qId: string) => {
    if (!isLocalBroadcaster) return;
    await updateDoc(doc(db, 'broadcast_questions', qId), { 
        isAnswered: true, 
        isHighlighted: false 
    });
  };

  if (isVerifyingInvite) return <div className="h-screen bg-slate-950 flex flex-col items-center justify-center text-white"><div className="animate-spin text-4xl mb-4">⌛</div><p className="font-bold tracking-widest uppercase">Validating Stage Access...</p></div>;

  const PageContent = (
    <div className="flex flex-col h-full bg-slate-900 overflow-hidden">
      <header className="flex-shrink-0 px-6 py-4 bg-slate-800 border-b border-white/5 flex items-center justify-between backdrop-blur-xl z-50">
        <div className="flex items-center gap-4">
          <div className="bg-indigo-600 p-2 rounded-xl text-white shadow-lg shadow-indigo-500/20">📡</div>
          <div>
            <h2 className="text-white font-bold text-sm uppercase tracking-tight">Support Stage</h2>
            <p className="text-slate-500 text-[10px] font-black uppercase tracking-widest flex items-center gap-2">
                {activeSession ? <><span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse"></span> {isLocalBroadcaster ? `${viewerCount} VIEWING` : 'LIVE'}</> : "READY"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {activeSession && <Badge color="red">LIVE</Badge>}
          {isLocalBroadcaster && activeSession && <Button onClick={handleEndBroadcast} variant="danger" className="!py-1.5 !px-4 text-xs font-black">Stop Broadcast</Button>}
          {!activeSession && guestInvite && (
            isMediaReady ? <Button onClick={handleGoLive} className="!bg-emerald-600 !py-1.5 !px-4 text-xs font-black">Go Live</Button> : <Button onClick={handleEnterBackstage} variant="outline" className="!py-1.5 !px-4 text-xs font-black text-indigo-400 border-indigo-400/30">Connect Camera</Button>
          )}
        </div>
      </header>

      <div className="flex-1 flex flex-col lg:flex-row min-h-0 overflow-hidden">
        <main className="flex-1 bg-black relative flex items-center justify-center min-h-0 overflow-hidden">
          {activeSession && !lastEndedSession ? (
            <div className="w-full h-full relative flex items-center justify-center bg-slate-950">
              {playBlocked && !isLocalBroadcaster && (
                <div className="absolute inset-0 flex flex-col items-center justify-center z-20 bg-slate-950/80 backdrop-blur-sm">
                   <Button onClick={() => { videoElementRef.current?.play(); setPlayBlocked(false); }} className="animate-pulse !bg-indigo-600 !px-8 !py-4 font-black">Click to Join Stream</Button>
                </div>
              )}
              {isLocalBroadcaster && !localStream && (
                <div className="absolute inset-0 flex flex-col items-center justify-center z-20 bg-slate-950/90 text-indigo-400">
                   <p className="mb-4 text-white font-bold">Your camera is disconnected.</p>
                   <Button onClick={handleEnterBackstage} className="w-auto border border-white/20">Reconnect Camera</Button>
                </div>
              )}
              {connectionStatus !== 'connected' && !isLocalBroadcaster && (
                <div className="absolute inset-0 flex flex-col items-center justify-center z-10 bg-slate-950 text-indigo-400 text-sm font-bold">
                    <div className="w-16 h-16 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin mb-4"></div>
                    <p className="animate-pulse">Connecting to live feed...</p>
                </div>
              )}
              <video ref={videoElementRef} autoPlay playsInline muted={isLocalBroadcaster} className="w-full h-full object-contain" />
              
              <div className="absolute top-8 left-8 flex items-center gap-3">
                <div className="bg-black/60 backdrop-blur-xl px-5 py-2.5 rounded-3xl border border-white/10 text-white shadow-2xl">
                  <p className="text-[10px] font-black uppercase text-indigo-400 tracking-widest">{activeSession.hostName}</p>
                  <p className="font-bold text-sm">{activeSession.title}</p>
                </div>
              </div>
              
              {questions.find(q => q.isHighlighted) && (
                <div className="absolute bottom-10 left-1/2 -translate-x-1/2 w-full max-w-2xl px-6 animate-slide-up z-20">
                  <div className="bg-indigo-600/90 backdrop-blur-lg text-white p-8 rounded-[2.5rem] shadow-2xl border border-white/20 ring-1 ring-white/10">
                    <div className="flex items-center gap-2 mb-3">
                        <span className="bg-white/20 text-[8px] font-black px-2 py-0.5 rounded uppercase tracking-widest">Question on Stage</span>
                    </div>
                    <p className="text-xl md:text-2xl font-medium leading-snug italic mb-4">"{questions.find(q => q.isHighlighted)?.text}"</p>
                    <p className="text-[10px] font-black text-indigo-200 uppercase tracking-widest">— Asked by @{questions.find(q => q.isHighlighted)?.userName}</p>
                  </div>
                </div>
              )}
            </div>
          ) : hasEnteredBackstage && !lastEndedSession ? (
            <div className="w-full h-full relative bg-slate-950">
              <video ref={videoElementRef} autoPlay playsInline muted className="w-full h-full object-contain opacity-40 grayscale" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="bg-slate-900/80 backdrop-blur-2xl p-10 rounded-[2.5rem] border border-white/10 text-center shadow-2xl max-w-sm">
                  <p className="text-white font-bold text-2xl mb-2">Backstage Mode</p>
                  <p className="text-slate-400 text-sm mb-8 leading-relaxed">Check your camera and audio. You are currently private. No one can see you until you click <strong>Start Stream</strong>.</p>
                  <Button onClick={handleGoLive} className="w-full !bg-indigo-600 !py-4 font-black uppercase tracking-widest shadow-xl">Start Stream</Button>
                </div>
              </div>
            </div>
          ) : lastEndedSession ? (
            <div className="text-center animate-fade-in px-8 max-w-xl">
              <div className="w-24 h-24 bg-emerald-500/10 text-emerald-500 rounded-full flex items-center justify-center mx-auto mb-8 text-5xl border border-emerald-500/20">✓</div>
              <h3 className="text-white text-5xl font-heading font-bold mb-4">Broadcast Ended</h3>
              <p className="text-slate-300 text-xl leading-relaxed mb-10">Thank you for attending <strong>{lastEndedSession.title}</strong>.</p>
              {currentUser && <Button onClick={() => navigate('/dashboard')} variant="outline" className="!py-3 !px-8 text-slate-300 border-white/10 hover:bg-white/5">Return to Hub</Button>}
            </div>
          ) : (
            <div className="text-center animate-fade-in px-8 flex flex-col items-center justify-center h-full">
              {scheduledInfo ? (
                <div className="max-w-md w-full animate-slide-up">
                    <div className="bg-slate-800/50 backdrop-blur-xl p-10 rounded-[3rem] border border-white/10 shadow-2xl relative overflow-hidden">
                        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-indigo-500 to-purple-600"></div>
                        <div className="w-20 h-20 bg-indigo-600/20 rounded-full flex items-center justify-center mx-auto mb-8 text-4xl border border-indigo-500/30">📡</div>
                        <h3 className="text-indigo-400 font-black text-[10px] uppercase tracking-[0.3em] mb-4">Next Live Session</h3>
                        <p className="text-white text-3xl font-heading font-bold mb-2">{scheduledInfo.name}</p>
                        <div className="bg-slate-900/50 px-6 py-3 rounded-2xl border border-white/5 inline-block">
                             <p className="text-slate-300 text-sm font-bold">{scheduledInfo.time}</p>
                        </div>
                        {scheduledInfo.description && (
                            <p className="text-slate-400 text-sm mt-6 leading-relaxed max-w-xs mx-auto italic font-medium">
                                "{scheduledInfo.description}"
                            </p>
                        )}
                        <p className="text-slate-500 text-[10px] mt-8 leading-relaxed uppercase font-black tracking-widest opacity-50">Stage activates automatically when live</p>
                    </div>
                </div>
              ) : (
                <div className="max-w-md w-full animate-slide-up">
                    <div className="bg-slate-800/30 backdrop-blur-lg p-12 rounded-[3rem] border border-white/5 text-center">
                        <div className="w-16 h-16 bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-6 text-2xl border border-white/5">🧊</div>
                        <h3 className="text-white text-xl font-bold mb-4">Stage Awaiting Speaker</h3>
                        <p className="text-slate-400 text-sm leading-relaxed">We are currently awaiting the next scheduled speaker. We will let you know when the next live session is available.</p>
                        <div className="mt-8 flex justify-center gap-1">
                            <div className="w-1 h-1 bg-indigo-500 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                            <div className="w-1 h-1 bg-indigo-500 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                            <div className="w-1 h-1 bg-indigo-500 rounded-full animate-bounce"></div>
                        </div>
                    </div>
                </div>
              )}
            </div>
          )}
          {streamError && (
            <div className="absolute inset-0 bg-slate-950/90 flex items-center justify-center z-[100] p-8">
              <Card className="max-w-md w-full p-8 !bg-slate-900 border-rose-500/50 shadow-2xl text-center">
                <span className="text-5xl mb-6 block">⚠️</span>
                <p className="text-white font-bold mb-8">{streamError}</p>
                <Button onClick={() => { setStreamError(''); setIsMediaReady(false); setHasEnteredBackstage(false); }} className="w-full">Dismiss</Button>
              </Card>
            </div>
          )}
        </main>

        <aside className="w-full lg:w-96 bg-slate-800 flex flex-col flex-shrink-0 min-h-0 lg:border-l border-white/5 shadow-2xl">
          <div className="p-6 border-b border-white/5 bg-slate-800/80 sticky top-0 z-10 flex justify-between items-center backdrop-blur-md">
            <h3 className="text-white font-bold text-sm flex items-center gap-2"><span>💬</span> Live Q&A</h3>
            <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">{questions.length} QUESTIONS</span>
          </div>
          <div className="flex-1 overflow-y-auto p-5 space-y-4 bg-slate-900/40 custom-scrollbar">
            {questions.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-slate-600 text-center px-10">
                <span className="text-3xl mb-3">💨</span>
                <p className="text-[10px] font-black uppercase tracking-widest">No active questions</p>
              </div>
            ) : (
              questions.map(q => (
                <div key={q.id} className={`p-5 rounded-2xl border transition-all duration-300 ${q.isAnswered ? 'bg-slate-800/40 border-slate-700 opacity-40' : 'bg-slate-800 border-white/10 shadow-xl'} ${q.isHighlighted ? 'ring-2 ring-indigo-500 bg-indigo-900/30' : ''}`}>
                  <div className="flex justify-between items-start mb-2">
                    <span className="text-[10px] font-black text-indigo-400 tracking-wider">@{q.userName}</span>
                    {q.isHighlighted && <Badge color="blue">ON SCREEN</Badge>}
                  </div>
                  <p className="text-white text-sm leading-relaxed font-medium">{q.text}</p>
                  
                  {isLocalBroadcaster && !q.isAnswered && (
                    <div className="mt-4 flex gap-2 pt-4 border-t border-white/5">
                      <button onClick={() => toggleHighlight(q.id, q.isHighlighted)} className={`flex-1 text-[9px] font-black uppercase py-2 rounded-xl transition-all ${q.isHighlighted ? 'bg-indigo-600 text-white shadow-lg' : 'bg-indigo-50/10 text-indigo-400 hover:bg-indigo-600 hover:text-white'}`}>{q.isHighlighted ? 'Hide Overlay' : 'Highlight'}</button>
                      <button onClick={() => markQuestionDone(q.id)} className="bg-emerald-600/10 text-emerald-400 text-[9px] font-black uppercase py-2 px-4 rounded-xl hover:bg-emerald-600 hover:text-white transition-all">Mark Done</button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
          <div className="p-6 bg-slate-800 border-t border-white/5">
            {activeSession && !isLocalBroadcaster ? (
              <form onSubmit={handleSubmitQuestion} className="space-y-4">
                <textarea value={newQuestion} onChange={e => setNewQuestion(e.target.value)} placeholder="Ask a question..." maxLength={200} className="w-full bg-slate-900 border border-white/10 rounded-2xl p-4 text-sm text-white h-24 focus:ring-2 focus:ring-indigo-500 transition-all resize-none shadow-inner" />
                <div className="flex justify-between items-center px-1">
                  <span className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">{newQuestion.length}/200</span>
                  <Button type="submit" disabled={!newQuestion.trim() || isSubmitting} className="!py-2 !px-6 !text-[10px] font-black uppercase tracking-widest bg-indigo-600">Post Question</Button>
                </div>
              </form>
            ) : isLocalBroadcaster ? (
              <div className="p-4 text-center bg-indigo-950/40 rounded-2xl border border-indigo-500/20">
                <p className="text-indigo-400 text-[10px] font-black uppercase tracking-widest animate-pulse">Broadcaster Console Active</p>
              </div>
            ) : (
              <div className="p-4 text-center text-slate-600 text-[10px] font-black uppercase tracking-widest italic">Questions will open when stream starts</div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );

  if (currentUser && !guestInvite) return <Layout fluid>{PageContent}</Layout>;
  return <div className="h-screen bg-slate-900">{PageContent}</div>;
};

export default Broadcast;
